import { useKV } from "@/hooks/use-kv-adapter";
import { useIsMobile } from "@/hooks/use-mobile";
import { api } from "@/lib/api";
import { exportOrganizationData } from "@/lib/export";
import {
    Announcement,
    AttendanceRecord,
    CustomRole,
    DashboardLayout,
    EmployeeDocument,
    EmployeeOffboarding,
    EmployeeOnboardingChecklist,
    ExpenseReimbursement,
    GeofenceState,
    GeofenceZone,
    Holiday,
    LeaveRequest,
    ManagerDelegation,
    Notification,
    Organization,
    OrganizationDepartment,
    OrganizationInvitation,
    OrganizationLocation,
    OvertimeEntry,
    RoleAssignment,
    RoleAuditLog,
    ShiftSchedule,
    ShiftSwapRequest,
    TeamMember,
    TimesheetEntry,
    User,
    WeeklyGoal,
    WFHRequest,
} from "@/types";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface AppContextType {
  // UI State
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (value: boolean) => void;
  activeSection: string;
  setActiveSection: (section: string) => void;
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
  preferredLanguage: string;
  handleLanguageChange: (lang: string) => void;
  isOnline: boolean;
  isMobile: boolean;

  // Auth & Org State
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  isAuthenticated: boolean;
  organizations: Organization[];
  setOrganizations: (orgs: Organization[]) => void;
  currentOrgId: string | null;
  setCurrentOrgId: (id: string | null) => void;
  currentOrg: Organization | undefined;
  orgDepartments: OrganizationDepartment[];
  setOrgDepartments: (depts: OrganizationDepartment[]) => void;
  orgLocations: OrganizationLocation[];
  setOrgLocations: (locs: OrganizationLocation[]) => void;
  handleLogin: (user: User) => void;
  handleLogout: () => void;
  handleSwitchOrganization: (orgId: string) => void;

  // Feature Data
  attendanceRecords: AttendanceRecord[];
  setAttendanceRecords: (records: AttendanceRecord[]) => void;
  leaveRequests: LeaveRequest[];
  setLeaveRequests: (requests: LeaveRequest[]) => void;
  timesheetEntries: TimesheetEntry[];
  setTimesheetEntries: (entries: TimesheetEntry[]) => void;
  overtimeEntries: OvertimeEntry[];
  setOvertimeEntries: (entries: OvertimeEntry[]) => void;
  employees: User[];
  setEmployees: (users: User[]) => void;
  teamMembers: TeamMember[];
  setTeamMembers: (members: TeamMember[]) => void;
  notifications: Notification[];
  setNotifications: (notifs: Notification[]) => void;
  announcements: Announcement[];
  setAnnouncements: (announcements: Announcement[]) => void;
  organizationInvitations: OrganizationInvitation[];
  setOrganizationInvitations: (invitations: OrganizationInvitation[]) => void;
  customRoles: CustomRole[];
  setCustomRoles: (roles: CustomRole[]) => void;
  roleAssignments: RoleAssignment[];
  setRoleAssignments: (assignments: RoleAssignment[]) => void;
  roleAuditLogs: RoleAuditLog[];
  setRoleAuditLogs: (logs: RoleAuditLog[]) => void;
  employeeOnboardingChecklists: EmployeeOnboardingChecklist[];
  setEmployeeOnboardingChecklists: (
    checklists: EmployeeOnboardingChecklist[],
  ) => void;
  shiftSchedules: ShiftSchedule[];
  setShiftSchedules: (schedules: ShiftSchedule[]) => void;
  shiftSwapRequests: ShiftSwapRequest[];
  setShiftSwapRequests: (requests: ShiftSwapRequest[]) => void;
  employeeDocuments: EmployeeDocument[];
  setEmployeeDocuments: (docs: EmployeeDocument[]) => void;
  expenseReimbursements: ExpenseReimbursement[];
  setExpenseReimbursements: (expenses: ExpenseReimbursement[]) => void;
  wfhRequests: WFHRequest[];
  setWfhRequests: (requests: WFHRequest[]) => void;
  employeeOffboardings: EmployeeOffboarding[];
  setEmployeeOffboardings: (offboardings: EmployeeOffboarding[]) => void;
  managerDelegations: ManagerDelegation[];
  setManagerDelegations: (delegations: ManagerDelegation[]) => void;
  holidays: Holiday[];
  setHolidays: (holidays: Holiday[]) => void;
  performanceReviews: any[];
  setPerformanceReviews: (reviews: any[]) => void;

  // Dashboard & Metrics
  dashboardLayout: DashboardLayout;
  setDashboardLayout: (layout: DashboardLayout) => void;
  attendanceRate: number;
  streak: number;
  setStreak: (streak: number | ((prev: number) => number)) => void;
  totalLeaveBalance: number;
  usedLeaveBalance: number;
  weeklyGoals: WeeklyGoal[];
  setWeeklyGoals: (goals: WeeklyGoal[]) => void;
  isCheckedIn: boolean;
  setIsCheckedIn: (checkedIn: boolean) => void;
  isOnBreak: boolean;
  setIsOnBreak: (onBreak: boolean) => void;
  breakStartTime: string | null;
  setBreakStartTime: (time: string | null) => void;
  totalBreakTime: number;
  setTotalBreakTime: (time: number | ((prev: number) => number)) => void;
  geofenceState: GeofenceState;
  setGeofenceState: (state: GeofenceState) => void;
  geofenceZones: GeofenceZone[];
  setGeofenceZones: (zones: GeofenceZone[]) => void;

  // Dialog States
  leaveDialogOpen: boolean;
  setLeaveDialogOpen: (open: boolean) => void;
  profileDialogOpen: boolean;
  setProfileDialogOpen: (open: boolean) => void;
  // ... Add other dialog states as needed

  // Actions
  handleExportOrganizationData: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useKV<boolean>(
    "sidebar-collapsed",
    false,
  );
  const [rawActiveSection, setRawActiveSection] = useKV<string>(
    "active-section",
    "dashboard",
  );

  const validSections = [
    "dashboard",
    "announcements",
    "onboarding",
    "offboarding",
    "attendance",
    "leaves",
    "holidays",
    "shifts",
    "reports",
    "employees",
    "core-hr",
    "lms",
    "org-chart",
    "performance",
    "team-calendar",
    "team-attendance",
    "timesheet",
    "learning",
    "approvals",
    "profile",
    "settings",
    "users",
    "organization",
    "documents",
    "expenses",
    "wfh",
    "recognition",
    "rewards",
    "surveys",
    "courses",
    "lifecycle",
    "recruitment",
    "payroll",
    "analytics",
  ];

  const activeSection = validSections.includes(rawActiveSection)
    ? rawActiveSection
    : "dashboard";

  const setActiveSection = (section: string) => {
    if (validSections.includes(section)) {
      setRawActiveSection(section);
    } else {
      console.warn(`Invalid section "${section}", defaulting to dashboard`);
      setRawActiveSection("dashboard");
    }
  };

  // Theme state
  const [theme, setTheme] = useKV<"light" | "dark">(
    "theme-preference",
    "light",
  );

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.setAttribute("data-appearance", "dark");
    } else {
      root.removeAttribute("data-appearance");
    }
  }, [theme]);

  // Language persistence
  const [preferredLanguage, setPreferredLanguage] = useKV<string>(
    "preferred-language",
    "en",
  );

  useEffect(() => {
    if (preferredLanguage && ["en", "es", "fr"].includes(preferredLanguage)) {
      if (i18n.language !== preferredLanguage) {
        i18n.changeLanguage(preferredLanguage);
      }
    }
  }, [preferredLanguage, i18n]);

  const handleLanguageChange = (language: string) => {
    if (["en", "es", "fr"].includes(language)) {
      setPreferredLanguage(language);
      toast.success(
        `Language changed to ${language === "en" ? "English" : language === "es" ? "Español" : "Français"}`,
      );
    }
  };

  // Offline/Online detection
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("🌐 Back online! All features are available.");
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning("📡 You are offline. Some features may be limited.", {
        duration: 5000,
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Auth State
  const [currentUser, setCurrentUser] = useKV<User | null>(
    "current-user",
    null,
  );
  const isAuthenticated = !!currentUser;

  // Organization State
  const [organizations, setOrganizations] = useKV<Organization[]>(
    "organizations",
    [],
  );
  const [currentOrgId, setCurrentOrgId] = useKV<string | null>(
    "current-organization-id",
    null,
  );
  const [orgDepartments, setOrgDepartments] = useKV<OrganizationDepartment[]>(
    "organization-departments",
    [],
  );
  const [orgLocations, setOrgLocations] = useKV<OrganizationLocation[]>(
    "organization-locations",
    [],
  );

  const currentOrg = organizations.find((org) => org.id === currentOrgId);

  // Feature Data
  const [attendanceRecords, setAttendanceRecords] = useKV<AttendanceRecord[]>(
    "attendance-records",
    [],
  );
  const [leaveRequests, setLeaveRequests] = useKV<LeaveRequest[]>(
    "leave-requests",
    [],
  );
  const [timesheetEntries, setTimesheetEntries] = useKV<TimesheetEntry[]>(
    "timesheet-entries",
    [],
  );
  const [overtimeEntries, setOvertimeEntries] = useKV<OvertimeEntry[]>(
    "overtime-entries",
    [],
  );
  const [employees, setEmployees] = useKV<User[]>("employees", []);
  const [notifications, setNotifications] = useKV<Notification[]>(
    "notifications",
    [],
  );
  const [announcements, setAnnouncements] = useKV<Announcement[]>(
    "announcements",
    [],
  );
  const [employeeDocuments, setEmployeeDocuments] = useKV<EmployeeDocument[]>(
    "employee-documents",
    [],
  );
  const [shiftSchedules, setShiftSchedules] = useKV<ShiftSchedule[]>(
    "shift-schedules",
    [],
  );
  const [holidays, setHolidays] = useKV<Holiday[]>("company-holidays", []);
  const [performanceReviews, setPerformanceReviews] = useKV<any[]>(
    "performance-reviews",
    [],
  );
  const [expenseReimbursements, setExpenseReimbursements] = useKV<
    ExpenseReimbursement[]
  >("expense-reimbursements", []);
  const [wfhRequests, setWfhRequests] = useKV<WFHRequest[]>("wfh-requests", []);
  const [organizationInvitations, setOrganizationInvitations] = useKV<
    OrganizationInvitation[]
  >("organization-invitations", []);
  const [customRoles, setCustomRoles] = useKV<CustomRole[]>("custom-roles", []);
  const [roleAssignments, setRoleAssignments] = useKV<RoleAssignment[]>(
    "role-assignments",
    [],
  );
  const [roleAuditLogs, setRoleAuditLogs] = useKV<RoleAuditLog[]>(
    "role-audit-logs",
    [],
  );
  const [employeeOnboardingChecklists, setEmployeeOnboardingChecklists] = useKV<
    EmployeeOnboardingChecklist[]
  >("employee-onboarding-checklists", []);
  const [shiftSwapRequests, setShiftSwapRequests] = useKV<ShiftSwapRequest[]>(
    "shift-swap-requests",
    [],
  );
  const [employeeOffboardings, setEmployeeOffboardings] = useKV<
    EmployeeOffboarding[]
  >("employee-offboardings", []);
  const [managerDelegations, setManagerDelegations] = useKV<
    ManagerDelegation[]
  >("manager-delegations", []);

  // Dashboard & Metrics State
  const [teamMembers, setTeamMembers] = useKV<TeamMember[]>("team-members", []);

  const getDefaultDashboardLayout = (): DashboardLayout => ({
    widgets: [
      {
        id: "total-employees",
        type: "total-employees",
        title: "Total Employees",
        visible: true,
        size: "medium",
        order: 0,
      },
      {
        id: "open-roles",
        type: "open-roles",
        title: "Open Roles",
        visible: true,
        size: "medium",
        order: 1,
      },
      {
        id: "attendance-rate",
        type: "attendance-rate",
        title: "Attendance Rate",
        visible: true,
        size: "medium",
        order: 2,
      },
      {
        id: "performance-trends",
        type: "performance-trends",
        title: "Performance Trends",
        visible: true,
        size: "large",
        order: 3,
      },
      {
        id: "workforce-diversity",
        type: "workforce-diversity",
        title: "Workforce Diversity",
        visible: true,
        size: "large",
        order: 4,
      },
      {
        id: "celebrations",
        type: "celebrations",
        title: "Celebrations",
        visible: true,
        size: "large",
        order: 5,
      },
      {
        id: "recent-activities",
        type: "recent-activities",
        title: "Recent Activities",
        visible: true,
        size: "large",
        order: 6,
      },
      {
        id: "pending-tasks",
        type: "pending-tasks",
        title: "Pending Tasks",
        visible: true,
        size: "large",
        order: 7,
      },
    ],
    lastModified: new Date().toISOString(),
  });

  const [dashboardLayout, setDashboardLayout] = useKV<DashboardLayout>(
    "dashboard-layout",
    getDefaultDashboardLayout(),
  );

  const [streak, setStreak] = useKV<number>("attendance-streak", 0);
  const [totalLeaveBalance] = useKV<number>("total-leave-balance", 25);
  const [usedLeaveBalance] = useKV<number>("used-leave-balance", 0);
  const [weeklyGoals, setWeeklyGoals] = useKV<WeeklyGoal[]>("weekly-goals", []);
  const [isCheckedIn, setIsCheckedIn] = useKV<boolean>(
    "user-checked-in",
    false,
  );
  const [isOnBreak, setIsOnBreak] = useKV<boolean>("is-on-break", false);
  const [breakStartTime, setBreakStartTime] = useKV<string | null>(
    "break-start-time",
    null,
  );
  const [totalBreakTime, setTotalBreakTime] = useKV<number>(
    "total-break-time-today",
    0,
  );
  const [geofenceZones, setGeofenceZones] = useKV<GeofenceZone[]>(
    "geofence-zones",
    [],
  );
  const [geofenceState, setGeofenceState] = useKV<GeofenceState>(
    "geofence-state",
    {
      isEnabled: false,
      permissionStatus: "prompt",
      watchId: null,
      currentZoneId: null,
    },
  );

  const attendanceRate = Math.round((streak / new Date().getDate()) * 100) || 0;

  // Dialog States
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  // Auth Handlers
  const handleLogin = (user: User) => {
    setCurrentUser(user);
    if (user.organizationId && !currentOrgId) {
      setCurrentOrgId(user.organizationId);
    }
    // Sync token from localStorage to API instance
    api.refreshToken();
    toast.success("Signed in successfully");
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setProfileDialogOpen(false);
    api.logout();
    toast.success("Signed out successfully");
  };

  // Listen for auto-logout events from API
  useEffect(() => {
    const onLogout = () => {
      if (currentUser) {
        handleLogout();
        toast.error("Session expired. Please sign in again.");
      }
    };
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, [currentUser]);

  // Validate auth token on mount
  useEffect(() => {
    api.refreshToken();
    console.log("[AppContext] Auth token validation - has token:", api.hasToken());
  }, []);

  const handleSwitchOrganization = (orgId: string) => {
    const org = organizations.find((o) => o.id === orgId);
    if (!org) {
      toast.error("Organization not found");
      return;
    }
    setCurrentOrgId(orgId);
    toast.success(`Switched to ${org.name}`);
  };

  // Export Logic
  const handleExportOrganizationData = () => {
    if (!currentOrg || !currentOrgId) {
      toast.error("No organization selected");
      return;
    }

    try {
      const metadata = exportOrganizationData({
        organization: currentOrg,
        employees: (employees || []).filter(
          (e) => e.organizationId === currentOrgId,
        ),
        departments: (orgDepartments || []).filter(
          (d) => d.organizationId === currentOrgId,
        ),
        locations: (orgLocations || []).filter(
          (l) => l.organizationId === currentOrgId,
        ),
        attendance: (attendanceRecords || []).filter((a) => {
          const emp = employees.find((e) => e.id === a.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        leaves: (leaveRequests || []).filter((l) => {
          const emp = employees.find((e) => e.id === l.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        timesheets: (timesheetEntries || []).filter((t) => {
          const emp = employees.find((e) => e.id === t.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        shifts: (shiftSchedules || []).filter((s) => {
          const emp = employees.find((e) => e.id === s.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        holidays: (holidays || []).filter(
          (h) => h.organizationId === currentOrgId,
        ),
        performances: (performanceReviews || []).filter((p) => {
          const emp = employees.find((e) => e.id === p.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        documents: (employeeDocuments || []).filter((d) => {
          const emp = employees.find((e) => e.id === d.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        expenses: (expenseReimbursements || []).filter((e) => {
          const emp = employees.find((em) => em.id === e.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
        wfhRequests: (wfhRequests || []).filter((w) => {
          const emp = employees.find((e) => e.id === w.employeeId);
          return emp?.organizationId === currentOrgId;
        }),
      });

      toast.success(
        `Organization data exported successfully! ${metadata.totalEmployees} employees, ${metadata.totalAttendanceRecords} attendance records.`,
        { duration: 5000 },
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export organization data");
    }
  };

  const value = {
    sidebarCollapsed,
    setSidebarCollapsed,
    activeSection,
    setActiveSection,
    theme,
    setTheme,
    preferredLanguage,
    handleLanguageChange,
    isOnline,
    isMobile,
    currentUser,
    setCurrentUser,
    isAuthenticated,
    organizations,
    setOrganizations,
    currentOrgId,
    setCurrentOrgId,
    currentOrg,
    orgDepartments,
    setOrgDepartments,
    orgLocations,
    setOrgLocations,
    handleLogin,
    handleLogout,
    handleSwitchOrganization,
    attendanceRecords,
    setAttendanceRecords,
    leaveRequests,
    setLeaveRequests,
    timesheetEntries,
    setTimesheetEntries,
    overtimeEntries,
    setOvertimeEntries,
    employees,
    setEmployees,
    notifications,
    setNotifications,
    announcements,
    setAnnouncements,
    teamMembers,
    setTeamMembers,
    dashboardLayout,
    setDashboardLayout,
    attendanceRate,
    streak,
    setStreak,
    totalLeaveBalance,
    usedLeaveBalance,
    weeklyGoals,
    setWeeklyGoals,
    isCheckedIn,
    setIsCheckedIn,
    isOnBreak,
    setIsOnBreak,
    breakStartTime,
    setBreakStartTime,
    totalBreakTime,
    setTotalBreakTime,
    geofenceState,
    setGeofenceState,
    geofenceZones,
    setGeofenceZones,
    leaveDialogOpen,
    setLeaveDialogOpen,
    profileDialogOpen,
    setProfileDialogOpen,
    organizationInvitations,
    setOrganizationInvitations,
    customRoles,
    setCustomRoles,
    roleAssignments,
    setRoleAssignments,
    roleAuditLogs,
    setRoleAuditLogs,
    employeeOnboardingChecklists,
    setEmployeeOnboardingChecklists,
    shiftSchedules,
    setShiftSchedules,
    shiftSwapRequests,
    setShiftSwapRequests,
    employeeDocuments,
    setEmployeeDocuments,
    expenseReimbursements,
    setExpenseReimbursements,
    wfhRequests,
    setWfhRequests,
    employeeOffboardings,
    setEmployeeOffboardings,
    managerDelegations,
    setManagerDelegations,
    holidays,
    setHolidays,
    performanceReviews,
    setPerformanceReviews,
    handleExportOrganizationData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
